import React from "react";

// Quality Types
export type QualityCategory = "Branding" | "Content" | "Compliance" | "Legal";
export type QualityStatus = "Healthy" | "Stable" | "Critical";
export type ContentQualityState = "Healthy" | "Stable" | "Critical";

export interface QualityMetric {
  category: QualityCategory;
  status: QualityStatus;
  progress: number;
}

// Audience Fit Types
export interface AudienceFitMetric {
  label: string;
  value: number;
}

export interface AudienceFitData {
  confidenceValue: number;
  status: "Healthy" | "Stable" | "Critical" | "Fair" | "Risk";
  metrics: AudienceFitMetric[];
  worksWell: string[];
}

// Recommendation Types
export enum RecommendationVariant {
  BLOCKER = "BLOCKER",
  WARNING = "WARNING",
  INFO = "INFO",
}

export interface RecommendationItem {
  id: string;
  title?: string;
  description: string;
  isSelected?: boolean;
  isDisabled?: boolean;
}

export interface TileRecommendation {
  id: string;
  variant: RecommendationVariant;
  label?: string;
  items: RecommendationItem[];
}

export interface RecommendationBlock {
  id: string;
  variant: RecommendationVariant;
  icon?: React.ReactNode;
  title: string;
  recommendations: TileRecommendation[];
}

export interface TabContent {
  id: string;
  label: string;
  blocks: RecommendationBlock[];
}

// Sticky Recommendations Types
export interface StickyRecommendationsData {
  audienceFitCount: number;
  audienceFitTotal: number;
  contentQualityCount: number;
  contentQualityTotal: number;
}

// Main Widget Props
export interface ContentQualityWidgetProps {
  /**
   * Content quality state (Healthy, Stable, Critical)
   */
  contentQualityState: ContentQualityState;
  /**
   * Number of filled segments in the health bar (0-5)
   */
  contentQualityFilledSegments: number;
  /**
   * Quality metrics cards displayed below the header
   */
  qualityMetrics: QualityMetric[];
  /**
   * Audience fit data (optional - if null/undefined, audience fit section is hidden)
   */
  audienceFit?: AudienceFitData | null;
  /**
   * Sticky recommendations card data
   */
  stickyRecommendations: StickyRecommendationsData;
  /**
   * Tabs content for recommendations
   */
  tabsContent: TabContent[];
  /**
   * Callback when apply button is clicked
   */
  onApplyClick?: () => void;
  /**
   * Callback when a recommendation is toggled
   */
  onRecommendationToggle?: (id: string, selected: boolean) => void;
  /**
   * Whether the modal is open
   */
  isOpen: boolean;
  /**
   * Callback to close the modal
   */
  onClose: () => void;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Whether the apply button should be disabled (e.g., for previous versions where recommendations were already applied)
   */
  isApplyDisabled?: boolean;
}
